const COOKIEYES_CONSENT_EVENTS = ['cookieyes_banner_load', 'cookieyes_consent_update'] as const
const ANALYTICS_CATEGORY_KEYS = ['analytics', 'performance'] as const

type ConsentListener = () => void

interface CookieYesAcceptedRejectedShape {
  accepted?: unknown
  rejected?: unknown
}

function normalizeCategorySet(value: unknown): Set<string> {
  if (!Array.isArray(value)) {
    return new Set()
  }

  return new Set(
    value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim().toLowerCase())
      .filter((entry) => entry.length > 0),
  )
}

function parseBooleanLike(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    if (value === 1) {
      return true
    }
    if (value === 0) {
      return false
    }
    return null
  }

  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().toLowerCase()
  if (['yes', 'true', 'accepted', 'allow', 'allowed', 'granted'].includes(normalized)) {
    return true
  }
  if (['no', 'false', 'rejected', 'deny', 'denied'].includes(normalized)) {
    return false
  }

  return null
}

function parseAcceptedRejectedShape(value: unknown): boolean | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const shapedValue = value as CookieYesAcceptedRejectedShape
  const acceptedCategories = normalizeCategorySet(shapedValue.accepted)
  const rejectedCategories = normalizeCategorySet(shapedValue.rejected)

  const hasAcceptedAnalyticsCategory = ANALYTICS_CATEGORY_KEYS.some((categoryKey) =>
    acceptedCategories.has(categoryKey),
  )
  if (hasAcceptedAnalyticsCategory) {
    return true
  }

  const hasRejectedAnalyticsCategory = ANALYTICS_CATEGORY_KEYS.some((categoryKey) =>
    rejectedCategories.has(categoryKey),
  )
  if (hasRejectedAnalyticsCategory) {
    return false
  }

  return null
}

function parseCategoryFlag(value: unknown, categoryKey: string): boolean | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const shapedValue = value as Record<string, unknown>
  const directCategoryValue = shapedValue[categoryKey]

  const directFlag = parseBooleanLike(directCategoryValue)
  if (directFlag !== null) {
    return directFlag
  }

  if (directCategoryValue && typeof directCategoryValue === 'object') {
    const nestedCategoryValue = directCategoryValue as Record<string, unknown>
    const nestedFlag =
      parseBooleanLike(nestedCategoryValue.consent) ??
      parseBooleanLike(nestedCategoryValue.status) ??
      parseBooleanLike(nestedCategoryValue.value) ??
      parseBooleanLike(nestedCategoryValue.enabled)
    if (nestedFlag !== null) {
      return nestedFlag
    }
  }

  return null
}

function parseCookieYesConsent(value: unknown): boolean | null {
  const acceptedRejectedFlag = parseAcceptedRejectedShape(value)
  if (acceptedRejectedFlag !== null) {
    return acceptedRejectedFlag
  }

  const directCategoryFlag = ANALYTICS_CATEGORY_KEYS.reduce<boolean | null>((resolved, key) => {
    if (resolved !== null) {
      return resolved
    }
    return parseCategoryFlag(value, key)
  }, null)
  if (directCategoryFlag !== null) {
    return directCategoryFlag
  }

  if (!value || typeof value !== 'object') {
    return null
  }

  const shapedValue = value as Record<string, unknown>
  const categoriesFlag = ANALYTICS_CATEGORY_KEYS.reduce<boolean | null>((resolved, key) => {
    if (resolved !== null) {
      return resolved
    }
    return parseCategoryFlag(shapedValue.categories, key)
  }, null)

  return categoriesFlag
}

function readCookieYesConsentFromWindow(): boolean | null {
  if (typeof window === 'undefined' || typeof window.getCkyConsent !== 'function') {
    return null
  }

  try {
    const consentPayload = window.getCkyConsent()
    return parseCookieYesConsent(consentPayload)
  } catch {
    return null
  }
}

declare global {
  interface Window {
    getCkyConsent?: () => unknown
  }
}

export class CookieYesConsentService {
  private static instance: CookieYesConsentService
  private listeners = new Set<ConsentListener>()
  private initializationAttempted = false
  private analyticsConsentGranted = false

  private constructor() {}

  public static getInstance(): CookieYesConsentService {
    if (!CookieYesConsentService.instance) {
      CookieYesConsentService.instance = new CookieYesConsentService()
    }

    return CookieYesConsentService.instance
  }

  public isAnalyticsConsentGranted(): boolean {
    this.ensureInitialized()
    return this.analyticsConsentGranted
  }

  public subscribe(listener: ConsentListener): () => void {
    this.ensureInitialized()
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  private ensureInitialized(): void {
    if (this.initializationAttempted) {
      return
    }

    this.initializationAttempted = true

    if (typeof document === 'undefined') {
      return
    }

    for (const eventName of COOKIEYES_CONSENT_EVENTS) {
      document.addEventListener(eventName, this.handleCookieYesConsentEvent as EventListener)
    }

    this.refreshConsentState()
  }

  private handleCookieYesConsentEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<unknown>
    const parsedFromEvent = parseCookieYesConsent(customEvent.detail)
    if (parsedFromEvent !== null) {
      this.updateAnalyticsConsent(parsedFromEvent)
      return
    }

    this.refreshConsentState()
  }

  private refreshConsentState(): void {
    const parsedConsentState = readCookieYesConsentFromWindow()
    this.updateAnalyticsConsent(parsedConsentState ?? false)
  }

  private updateAnalyticsConsent(nextValue: boolean): void {
    if (this.analyticsConsentGranted === nextValue) {
      return
    }

    this.analyticsConsentGranted = nextValue

    for (const listener of this.listeners) {
      listener()
    }
  }
}

export const cookieYesConsentService = CookieYesConsentService.getInstance()
